/**
 * =============================================================================
 * SOLUTION TOGGLE COMPONENT
 * =============================================================================
 *
 * Toggle button to mark/unmark a reply as the solution.
 * Only APPROVED replies can be marked as solution.
 *
 * GAP-008: Admin Reply & Solution Management UI
 *
 * =============================================================================
 */

import React from 'react';
import { Button, Tooltip } from '@bigcommerce/big-design';
import { StarIcon, StarBorderIcon } from '@bigcommerce/big-design-icons';
import { tokens } from '../../tokens';

/**
 * Props for SolutionToggle component.
 */
export interface SolutionToggleProps {
  /** Reply ID */
  replyId: string;
  /** Whether the reply is currently marked as solution */
  isSolution: boolean;
  /** Whether the reply is approved (only approved replies can be solutions) */
  isApproved: boolean;
  /** Callback when toggle is clicked */
  onToggle: (replyId: string, isSolution: boolean) => Promise<void>;
  /** Whether a toggle action is in progress */
  isPending: boolean;
  /** Whether another solution control owns the panel-wide action slot */
  disabled?: boolean;
  /** Optional test ID */
  testId?: string;
}

/**
 * Toggle button to mark/unmark a reply as the solution.
 *
 * Visual design:
 * - Unmarked: Outlined star icon with "Mark as Solution" tooltip
 * - Marked: Filled star icon (gold) with "Remove Solution" tooltip
 * - Disabled: Grayed out for non-approved replies
 * - Loading: Button shows loading spinner during toggle
 *
 * @example
 * ```tsx
 * <SolutionToggle
 *   replyId="r_abc123"
 *   isSolution={false}
 *   isApproved={true}
 *   onToggle={handleToggle}
 *   isPending={false}
 * />
 * ```
 */
export function SolutionToggle({
  replyId,
  isSolution,
  isApproved,
  onToggle,
  isPending,
  disabled = false,
  testId,
}: SolutionToggleProps): React.ReactElement {
  const handleClick = async (): Promise<void> => {
    if (isPending || disabled || !isApproved) return;
    await onToggle(replyId, isSolution);
  };

  const tooltipText = !isApproved
    ? 'Only approved replies can be marked as solution'
    : disabled && !isPending
      ? 'Another solution change is in progress'
    : isSolution
      ? 'Remove Solution'
      : 'Mark as Solution';

  const ariaLabel = !isApproved
    ? 'Solution toggle disabled — reply not approved'
    : isPending
      ? 'Updating solution, please wait'
    : isSolution
      ? 'Remove solution'
      : 'Mark as solution';

  return (
    <Tooltip trigger={
      <Button
        variant="subtle"
        onClick={handleClick}
        disabled={!isApproved || isPending || disabled}
        isLoading={isPending}
        iconOnly={
          isSolution
            ? <StarIcon color={isApproved ? tokens.color.status.warning.accent : undefined} />
            : <StarBorderIcon />
        }
        aria-label={ariaLabel}
        data-solution-reply-id={replyId}
        data-testid={testId}
      />
    } placement="top">
      {tooltipText}
    </Tooltip>
  );
}
